import React from 'react';
import Match from './Match';
import './TeamRoster.css';

// Display order + friendly labels for position groups.
const GROUPS = [
  ['GK', 'Goalkeepers'],
  ['DF', 'Defenders'],
  ['MF', 'Midfielders'],
  ['FW', 'Forwards'],
  ['OTHER', 'Other'],
];

function groupKey(pos) {
  const p = (pos || '').toUpperCase();
  return ['GK', 'DF', 'MF', 'FW'].includes(p) ? p : 'OTHER';
}

function TeamRoster({ team, onSelectPlayer, onSelectTeam }) {
  if (!team) return null;

  const results = (team.results || []).map(r => ({
    date: r.match_date,
    status: r.status,
    home: { name: r.home_name, id: r.home_team_id, logo_url: r.home_logo },
    away: { name: r.away_name, id: r.away_team_id, logo_url: r.away_logo },
    home_score: r.home_score,
    away_score: r.away_score,
  }));

  // A squad lists a player once per season — keep the most recent entry
  // (rows arrive season-DESC) so each player appears once.
  const seen = new Set();
  const players = [];
  for (const s of team.squad || []) {
    if (seen.has(s.player_id)) continue;
    seen.add(s.player_id);
    players.push(s);
  }

  const buckets = new Map(GROUPS.map(([k]) => [k, []]));
  for (const p of players) buckets.get(groupKey(p.position || p.squad_position)).push(p);
  for (const list of buckets.values()) {
    list.sort((a, b) => {
      const an = a.jersey_number ?? 999;
      const bn = b.jersey_number ?? 999;
      if (an !== bn) return an - bn;
      return a.name.localeCompare(b.name);
    });
  }

  return (
    <div className="roster">
      <div className="roster-header">
        <img
          src={team.logo_url || '/team-placeholder.svg'}
          alt=""
          className="roster-logo"
          onError={(e) => { e.target.src = '/team-placeholder.svg'; }}
        />
        <div className="roster-title">
          <h2>{team.name}</h2>
          <span className="roster-sub">
            {team.team_type === 'national' ? 'National team' : 'Club'} · {players.length} players
          </span>
        </div>
      </div>

      {results.length > 0 && (
        <section className="roster-group">
          <div className="roster-group-label">World Cup 2026 <span className="roster-group-count">{results.length}</span></div>
          <div className="roster-results">
            {results.map((m, i) => <Match key={i} match={m} onSelectTeam={onSelectTeam} />)}
          </div>
        </section>
      )}

      {GROUPS.map(([key, label]) => {
        const list = buckets.get(key);
        if (!list.length) return null;
        return (
          <section className="roster-group" key={key}>
            <div className="roster-group-label">{label} <span className="roster-group-count">{list.length}</span></div>
            <div className="roster-grid">
              {list.map((p) => (
                <button
                  key={p.player_id}
                  className="player-card"
                  onClick={() => onSelectPlayer({
                    id: p.player_id, name: p.name, image_url: p.image_url,
                    position: p.position, nationality: p.nationality,
                  })}
                >
                  {p.jersey_number != null && <span className="card-number">{p.jersey_number}</span>}
                  <img
                    src={p.image_url || '/person-placeholder.svg'}
                    alt=""
                    className="card-photo"
                    loading="lazy"
                    style={{ objectPosition: p.image_url ? (p.face_focus || 'center 20%') : 'center' }}
                    onError={(e) => { e.target.src = '/person-placeholder.svg'; }}
                  />
                  <div className="card-name">{p.name}</div>
                  <div className="card-pos">{p.squad_position || p.position || ''}</div>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default TeamRoster;
